
import { 
    AuthResponse, 
    Connector, 
    ConnectFormResponse, 
    Account,
    PostsResponse,
    OAuthStartResponse,
    OAuthStatusResponse,
    ConnectorDetails,
    PublishPostRequest,
    FileUploadResponse,
    PostStatus,
    ModerationStatus,
    AccessRecord,
    SearchEntity,
    PostHistoryItem,
    AccessInfo
} from "../types";

const API_BASE = import.meta.env.VITE_API_BASE || "https://omedia-socials.nebo.me/api";

let jwtToken: string | null = null;

export const setJwt = (token: string) => {
    jwtToken = token;
};

const getHeaders = (isMultipart = false, skipContentType = false) => {
    const headers: Record<string, string> = {
        'Authorization': `Bearer ${jwtToken}`,
    };
    if (!isMultipart && !skipContentType) {
        headers['Content-Type'] = 'application/json';
    }
    return headers;
};

const request = async <T>(endpoint: string, config: RequestInit & { isMultipart?: boolean; skipContentType?: boolean } = {}): Promise<T> => {
    const { isMultipart, skipContentType, ...fetchOptions } = config;
    const url = endpoint.startsWith('http') ? endpoint : `${API_BASE}${endpoint}`;

    let headers = { 
        ...getHeaders(isMultipart, skipContentType), 
        ...(fetchOptions.headers as Record<string, string> || {}) 
    };

    let response = await fetch(url, { ...fetchOptions, headers });

    if (!response.ok) {
        let errorMsg = `Request failed with status ${response.status}`;
        try {
            const data = await response.json();
            errorMsg = data.error || data.message || errorMsg;
        } catch (e) {
            errorMsg = response.statusText || errorMsg;
        }
        console.error(`[API Error] ${config.method || 'GET'} ${endpoint}:`, errorMsg);
        throw new Error(errorMsg);
    }

    if (response.status === 204) return {} as T;
    
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
        return response.json();
    }

    return {} as T;
};

// --- Auth ---
export const apiLogin = async (bxAuthData: { accessToken: string; refreshToken: string; domain: string }): Promise<AuthResponse> => {
    return request<AuthResponse>('/auth/', {
        method: "POST",
        body: JSON.stringify(bxAuthData),
    });
};

// --- Access Control ---
export const grantAccess = (data: { entityId: string, role: 'creator' | 'moderator' }) => {
    const endpoint = data.role === 'creator' ? '/access/creator' : '/access/moderator';
    return request(endpoint, { method: "POST", body: JSON.stringify({ entityId: data.entityId }) });
};

export const revokeAccess = (data: { entityId: string, role: 'creator' | 'moderator' }) => {
    const rolePath = data.role === 'creator' ? 'creator' : 'moderator';
    return request(`/access/${rolePath}`, { 
        method: "DELETE",
        body: JSON.stringify({ entityId: data.entityId })
    });
};

export const listAccess = (role?: string) => 
    request<{ result: AccessRecord[] }>(`/access/list${role ? `?role=${role}` : ''}`);

export const getAccessMe = () => 
    request<{ result: AccessInfo }>('/access/me');

export const searchEntities = (query: string, type: string = 'all') => 
    request<{ result: SearchEntity[] }>(`/access/entities/search?query=${encodeURIComponent(query)}&type=${type}`);

export const assignModerator = (data: { creatorEntityId: string, moderatorId: string }) => 
    request('/access/assign-moderator', { method: "POST", body: JSON.stringify(data) });

export const unassignModerator = (data: { creatorEntityId: string }) => 
    request('/access/assign-moderator', { 
        method: "DELETE",
        body: JSON.stringify({ creatorEntityId: data.creatorEntityId })
    });

// --- Connectors ---
export const getConnectors = () => request<{ result: Connector[] }>('/connectors/');
export const getConnectorDetails = (connectorId: string) => request<{ result: ConnectorDetails }>(`/connectors/${connectorId}/details`);
export const getConnectForm = (connectorId: string) => request<{ result: ConnectFormResponse }>(`/connectors/${connectorId}/connect-form`);
export const getAccounts = (connectorId: string) => request<{ result: { accounts: Account[] } }>(`/connectors/${connectorId}/accounts`);
export const connectAccount = (connectorId: string, fields: Record<string, string>) => request('/connectors/' + connectorId + '/accounts', { method: "POST", body: JSON.stringify({ fields }) });
export const updateAccount = (connectorId: string, accountId: string, fields: Record<string, string>) => request(`/connectors/${connectorId}/accounts/${accountId}`, { method: "PUT", body: JSON.stringify({ fields }) });
export const deleteAccount = (connectorId: string, accountId: string) => request(`/connectors/${connectorId}/accounts/${accountId}`, { method: "DELETE", skipContentType: true });

// --- OAuth ---
export const startOAuth = (connectorId: string) => request<OAuthStartResponse>(`/connectors/${connectorId}/oauth/start`);
export const checkOAuthStatus = (processId: string) => request<OAuthStatusResponse>(`/connectors/oauth/status/${processId}`);
export const finalizeOAuth = (processId: string, selectedIds: string[]) => request('/connectors/oauth/finalize', { method: "POST", body: JSON.stringify({ processId, selectedIds }) });

// --- Posts ---
export const getPosts = (params?: { status?: PostStatus | 'all'; moderationStatus?: ModerationStatus | 'all'; page?: number; limit?: number; }) => {
    const query = new URLSearchParams();
    if (params) {
        if (params.status && params.status !== 'all') query.append('status', params.status);
        if (params.moderationStatus && params.moderationStatus !== 'all') query.append('moderationStatus', params.moderationStatus);
        if (params.page) query.append('page', params.page.toString());
        if (params.limit) query.append('limit', params.limit.toString());
    }
    return request<PostsResponse>(`/posts${query.toString() ? `?${query.toString()}` : ''}`);
};
export const getPost = (id: string) => request<{ result: PostHistoryItem }>(`/posts/${id}`);

// Legacy fetch upload (kept for compatibility)
export const uploadFile = (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return request<FileUploadResponse>('/upload', { method: "POST", body: fd, isMultipart: true });
};

// New XHR upload for progress tracking
export const uploadFileWithProgress = (
    file: File, 
    onProgress: (percent: number) => void
): { promise: Promise<FileUploadResponse>, abort: () => void } => {
    const fd = new FormData();
    fd.append("file", file);
    
    const xhr = new XMLHttpRequest();
    let aborted = false;

    const promise = new Promise<FileUploadResponse>((resolve, reject) => {
        xhr.upload.addEventListener("progress", (event) => {
            if (event.lengthComputable) {
                const percent = Math.round((event.loaded / event.total) * 100);
                onProgress(percent);
            }
        });

        xhr.addEventListener("load", () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                try {
                    const response = JSON.parse(xhr.responseText);
                    resolve(response);
                } catch (e) {
                    reject(new Error("Invalid JSON response"));
                }
            } else {
                reject(new Error(`Upload failed with status ${xhr.status}`));
            }
        });

        xhr.addEventListener("error", () => reject(new Error("Network error during upload")));
        xhr.addEventListener("abort", () => reject(new Error("Upload cancelled")));

        xhr.open("POST", `${API_BASE}/upload`);
        if (jwtToken) {
            xhr.setRequestHeader("Authorization", `Bearer ${jwtToken}`);
        }
        xhr.send(fd);
    });

    return {
        promise,
        abort: () => {
            aborted = true;
            xhr.abort();
        }
    };
};

export const sendPost = (body: PublishPostRequest) => request('/posts', { method: "POST", body: JSON.stringify(body) });

// Moderation & Publishing
export const approvePost = (id: string) => request<{ result: PostHistoryItem }>(`/posts/${id}/approve`, { method: "POST", body: JSON.stringify({}) });
export const rejectPost = (id: string, comment?: string) => request<{ result: PostHistoryItem }>(`/posts/${id}/reject`, { 
    method: "POST", 
    body: JSON.stringify({ comment }) 
});
export const publishPost = (id: string) => request<{ results: any[] }>(`/posts/${id}/publish`, { method: "POST", body: JSON.stringify({}) });
